import { cloneDeep, isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import { HeaderBidderActiveActive } from '~/src/dash/api/console-api';
import {
  getItemsKey,
  IDashboardChartData,
  IDashboardHomeCardMetrics,
  IDashboardLiveData,
  queryHomeDashboardLiveData,
} from '~/src/dash/api/dashboards/home';
import { querySmallBidders } from '~/src/dash/api/filters/bidders';
import { getFilteredDemandSourcesList } from '~/src/dash/api/filters/demandSources';
import { MobileCards } from '~/src/dash/components/cards/MobileCards';
import { IDashboardChartMetric } from '~/src/dash/components/charts/common';
import { MobileCompareChart } from '~/src/dash/components/charts/MobileCompareChart';
import { MobileNativeSelect } from '~/src/dash/components/dropdowns/MobileNativeSelect';
import {
  FILTER_PILLS,
  FilterPillKey,
  formatSelectedChannelGroupIntoStringOfIDs,
  getMobileFilterParams,
} from '~/src/dash/components/filters/common';
import { ClockIcon, MobileFilterIcon } from '~/src/dash/components/icons/misc-icons';
import { Col, Cols, Div, Panel, PanelBreak, Panels, Row, Rows, Text } from '~/src/dash/publicaUI';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { findTimeRange } from '~/src/dash/utils/dateTimeRanges';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { backgroundColor } from '~/src/dash/utils/material-dashboard-react';
import { checkUserInfoMasked } from '~/src/dash/utils/roles';
import { getTimezone } from '~/src/dash/utils/timezone';
import {
  clearedActiveFilters,
  extractDataForMultiLineChart,
  extractViableLiveMetrics,
  getLiveCardGroup,
  updateDefaultSelectedForFilters,
  updateDisplayedMetricKeys,
} from '~/src/dash/views/dashboards/DashboardHome/common/common';
import { useMobileConfig, useMobileStore } from '~/src/dash/views/mobile/contexts';
import { EDashboardTab } from '~/src/dash/views/mobile/MobileBaseView/config';
import {
  EMobileFilter,
  isAnyFilterActive,
} from '~/src/dash/views/mobile/MobileBaseView/Home/common';
import { IMobileDashboardLiveConfig } from '~/src/dash/views/mobile/MobileBaseView/Home/configs/liveConfig';
import { IMobileDashboardOverviewConfig } from '~/src/dash/views/mobile/MobileBaseView/Home/configs/overviewConfig';
import { useMobileDashboardStore } from '~/src/dash/views/mobile/MobileBaseView/Home/contexts';
import { FilterModal } from '~/src/dash/views/mobile/MobileBaseView/Home/FilterModal';
import {
  FilterSelector,
  IMobileFilterSelection,
} from '~/src/dash/views/mobile/MobileBaseView/Home/FilterSelector';
import { MobileDashboardSubChart } from '~/src/dash/views/mobile/MobileBaseView/Home/MobileDashboardSubChart';
import { MobileFiltersRow } from '~/src/dash/views/mobile/MobileBaseView/Home/MobileFiltersRow';
import { MobileTimeRangeSelector } from '~/src/dash/views/mobile/MobileBaseView/Home/MobileTimeRangeSelector';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

const mobileFilterSelections = [
  // we start with 1 because 0 will represent none to the component
  { label: 'Filters', value: 1, SVGIcon: MobileFilterIcon },
  { label: 'Time Range', value: 2, SVGIcon: ClockIcon },
] as IMobileFilterSelection[];

const formatDataForMobileCompareChart = (main: Array<Record<string, any>>) => {
  const formattedData: Array<Record<string, any>> = [];
  if (isEmpty(main)) {
    return formattedData;
  }
  main.forEach((item) => {
    formattedData.push({
      main: item,
    });
  });
  return formattedData;
};

export const MobileDashboardLive = () => {
  const { Dashboards } = useMobileConfig();
  const { session, user, currencyType, isSuperUser } = useMobileStore();
  const defaultTimezone = getTimezone(user.user.Timezone, session.publisher.Timezone);
  const overviewCfg = Dashboards.Overview as IMobileDashboardOverviewConfig;
  const liveCfg = Dashboards.Live as IMobileDashboardLiveConfig;
  const { defaultState, cards, subCharts } = liveCfg;
  const { tabs, handleChangeTab } = useMobileDashboardStore();

  const initialLoadStart = moment().unix();
  const isInitialized = useRef(false);
  const isMounted = useRef(false);
  const [loadState, setLoadState] = useState(LoadingStateLoading);

  // filters
  const [filtersLoadState, setFiltersLoadState] = useState(LoadingStateLoading);
  const [filters, setFilters] = useState({} as Record<string, IMenuList>);
  const [selectedFilters, setSelectedFilters] = useState({} as Record<string, IMenuList>);

  // filter modal
  const [openFilter, setOpenFilter] = useState('');
  const [filterModalInfo, setFilterModalInfo] = useState({} as Record<string, IListItem>);

  // active filters
  const [activeFilters, setActiveFilters] = useState(overviewCfg.defaultState.activeFilters);
  const [cardGroup, setCardGroup] = useState(
    getLiveCardGroup(overviewCfg.defaultState.activeFilters),
  );

  // snackbar
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);

  const [activeFilterSection, setActiveFilterSection] = useState(
    isAnyFilterActive(overviewCfg.defaultState.activeFilters)
      ? EMobileFilter.Filters
      : EMobileFilter.None,
  );

  // calendar
  const timezone = defaultTimezone;
  const [timeRange, setTimeRange] = useState(defaultState.timeRange);

  const [displayedMetricKeys, setDisplayedMetricKeys] = useState(defaultState.displayedMetricKeys);
  const [activeChartMetrics, setActiveChartMetrics] = useState(defaultState.activeChartMetrics);

  const [data, setData] = useState({} as IDashboardLiveData);

  const isClearAllDisplayed = Object.values(selectedFilters).some((filter) => {
    return !isEmpty(filter);
  });

  // first we need to make sure all the filters are loaded
  useEffect(() => {
    getFilters();
    track('Mobile Dash Live - View', { isSuperUser }, user.user);
  }, []);

  // run the api call when any of these states change
  useEffect(() => {
    if (isMounted.current && filtersLoadState === LoadingStateHasData) {
      getHomeDashboardLiveData(); // TESTING
    }
  }, [filtersLoadState, selectedFilters, timeRange]);

  // update the config default selected for filters
  // TODO: update this to differentiate overview and live filters !important
  useEffect(() => {
    if (isMounted.current) {
      updateDefaultSelectedForFilters(
        selectedFilters,
        overviewCfg.filters,
        overviewCfg.defaultState,
      );
    }
    isMounted.current = true;
  }, [selectedFilters]);

  const getHomeDashboardLiveData = () => {
    setLoadState(LoadingStateLoading);
    const updatedCardGroup = getLiveCardGroup(activeFilters);
    const filterParams = getMobileFilterParams(selectedFilters, overviewCfg.filters, activeFilters);
    const query = {
      publisher_id: session.publisher.ID,
      timezone,
      currency_type: currencyType,
      live_density_range: timeRange,
      live_items_key: getItemsKey(activeFilters),
      ...filterParams,
    };
    queryHomeDashboardLiveData(session.publisher.ID, query)
      .then((result: any) => {
        unstable_batchedUpdates(() => {
          setData(result);
          setLoadState(LoadingStateHasData);
          if (cardGroup !== updatedCardGroup) {
            const updatedDisplayedMetricKeys = updateDisplayedMetricKeys(
              cards.groupings[updatedCardGroup],
              displayedMetricKeys,
            );
            const updatedActiveChartMetrics = [] as IDashboardChartMetric[];
            activeChartMetrics.forEach((metric) => {
              const metricKey = metric.key as keyof IDashboardHomeCardMetrics;
              if (updatedDisplayedMetricKeys.includes(metricKey)) {
                updatedActiveChartMetrics.push(metric);
              }
              if (isEmpty(updatedActiveChartMetrics)) {
                const newMetricKey = updatedDisplayedMetricKeys[0];
                const { label, valueType, chartType } = overviewCfg.cards.metrics[newMetricKey];
                updatedActiveChartMetrics.push({
                  key: newMetricKey,
                  label,
                  valueType,
                  chartColorIndex: 0,
                  chartType,
                });
              }
            });
            unstable_batchedUpdates(() => {
              setDisplayedMetricKeys(updatedDisplayedMetricKeys);
              setCardGroup(updatedCardGroup);
              setActiveChartMetrics(updatedActiveChartMetrics);
            });
            // update config default state
            defaultState.displayedMetricKeys = updatedDisplayedMetricKeys;
            defaultState.activeChartMetrics = updatedActiveChartMetrics;

            if (!isInitialized.current) {
              track(
                'Mobile Dash Live - Initial Data Load Time',
                { isSuperUser, initialLoadTime: moment().unix() - initialLoadStart },
                user.user,
              );
              isInitialized.current = true;
            }
          }
        });
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setData({
            LiveData: [] as IDashboardChartData[],
            CardMetrics: {} as IDashboardHomeCardMetrics,
          });
          setLoadState(LoadingStateNoData);
          setSnackbarError(true);
          setSnackbarMessage('Error retrieving data. Please wait and try again.');
        });
      });
  };

  // load all filters
  const getFilters = () => {
    setFiltersLoadState(LoadingStateLoading);
    let updatedFilters = {} as Record<FilterPillKey, IMenuList>;
    const updatedSelectedFilters = {} as Record<FilterPillKey, IMenuList>;
    const apiCalls = [] as Array<Promise<Record<string, IMenuList>>>;

    const filterKeys = overviewCfg.orderedFilterKeys as FilterPillKey[];

    filterKeys.forEach((key) => {
      const { list, getList } = FILTER_PILLS[key];
      // if the filter requires an api call, load it
      if (getList) {
        apiCalls.push(getList(session, user));
      } else if (list) {
        updatedFilters[key] = FILTER_PILLS[key].list || ({} as IMenuList);
      }
    });
    Promise.all(apiCalls).then((apiFilters) => {
      for (const filter of apiFilters) {
        updatedFilters = { ...updatedFilters, ...filter };
      }
      // if there are selected defaults, make them selected true here before continuing
      filterKeys.forEach((key) => {
        if (!isEmpty(overviewCfg.filters[key].defaultSelected)) {
          const defaultSelected = overviewCfg.filters[key].defaultSelected || [];
          defaultSelected.forEach((id) => {
            updatedFilters[key][id].selected = true;
            if (updatedSelectedFilters[key]) {
              updatedSelectedFilters[key][id] = updatedFilters[key][id];
            } else {
              updatedSelectedFilters[key] = {
                [id]: updatedFilters[key][id],
              };
            }
          });
        }
      });
      unstable_batchedUpdates(() => {
        setFilters(updatedFilters);
        setSelectedFilters(updatedSelectedFilters);
        setFiltersLoadState(LoadingStateHasData);
      });
    });
  };

  // this updates the bidders filter according to site ids
  const refreshBiddersFilter = async (siteIDs: string) => {
    const result = await querySmallBidders(session.publisher.ID, {
      publisher_id: session.publisher.ID,
      site_ids: siteIDs,
    });
    const biddersList: IMenuList = {};
    result.HeaderBidders.forEach((hb) => {
      const ID = hb.ID;
      const title = checkUserInfoMasked(user.user) ? `demo_bidder_${ID}` : hb.Name;
      biddersList[ID] = {
        title,
        selected: false,
        ID,
        active: hb.Active === HeaderBidderActiveActive,
      };
    });
    const refreshedSelectedBidders = {} as IMenuList;
    if (!isEmpty(selectedFilters.Bidders)) {
      Object.keys(selectedFilters.Bidders).forEach((id) => {
        if (biddersList[id]) {
          biddersList[id].selected = true;
          refreshedSelectedBidders[id] = biddersList[id];
        }
      });
    }
    return {
      selectedBidders: refreshedSelectedBidders,
      allBidders: biddersList,
    };
  };

  // this updates the demand sources filter according to site ids
  const refreshDemandSourcesFilter = async (siteIDs: string) => {
    const result = await getFilteredDemandSourcesList(session, user, siteIDs);
    const filteredDemandSources = result['Filtered Demand Sources'];
    const refreshedSelectedDemandSources = {} as IMenuList;
    if (!isEmpty(selectedFilters['Filtered Demand Sources'])) {
      Object.keys(selectedFilters['Filtered Demand Sources']).forEach((id) => {
        if (filteredDemandSources[id]) {
          filteredDemandSources[id].selected = true;
          refreshedSelectedDemandSources[id] = filteredDemandSources[id];
        }
      });
    }
    return {
      selectedDemandSources: refreshedSelectedDemandSources,
      allDemandSources: filteredDemandSources,
    };
  };

  const handleOpenFilter = (filterKey: string) => {
    unstable_batchedUpdates(() => {
      setOpenFilter(filterKey);
      setFilterModalInfo(filters[filterKey]);
    });
  };

  const handleCloseFilter = () => {
    unstable_batchedUpdates(() => {
      setOpenFilter('');
      setFilterModalInfo({} as Record<string, IListItem>);
    });
  };

  const handleAcceptFilter = async (
    key: string,
    allOptions: Record<string, IListItem>,
    selectedOptions: Record<string, IListItem>,
  ) => {
    const appliedFilter = overviewCfg.filters[key];
    const { category } = appliedFilter;
    const clonedActiveFilters = cloneDeep(activeFilters);
    if (!selectedFilters[key] && isEmpty(selectedOptions)) {
      appliedFilter.defaultSelected = [];
    } else if (!!selectedFilters[key] && isEmpty(selectedOptions)) {
      appliedFilter.defaultSelected = [];
      if (category && clonedActiveFilters[category] === key) {
        clonedActiveFilters[category] = '';
      }
    } else {
      appliedFilter.defaultSelected = Object.values(selectedOptions).map((item) => item.ID);
      if (category) clonedActiveFilters[category] = key;
    }

    // custom block for refreshing bidders & demand sources
    const updatedSelectedFilters = {} as Record<string, IMenuList>;
    const updatedFilters = {} as Record<string, IMenuList>;
    if (category === 'supply') {
      let siteIDs = '';
      if (key === 'Channels') {
        siteIDs = Object.keys(selectedOptions).join(',');
      }
      if (key === 'Channel Groups') {
        siteIDs = formatSelectedChannelGroupIntoStringOfIDs(selectedOptions);
      }
      const results = await Promise.all([
        refreshBiddersFilter(siteIDs),
        refreshDemandSourcesFilter(siteIDs),
      ]);
      const { selectedBidders, allBidders } = results[0];
      const { selectedDemandSources, allDemandSources } = results[1];
      updatedSelectedFilters.Bidders = selectedBidders;
      updatedFilters.Bidders = allBidders;
      if (isEmpty(selectedBidders) && activeFilters.demand === 'Bidders') {
        clonedActiveFilters.demand = '';
      }
      updatedSelectedFilters['Filtered Demand Sources'] = selectedDemandSources;
      updatedFilters['Filtered Demand Sources'] = allDemandSources;
      if (isEmpty(selectedDemandSources) && activeFilters.demand === 'Filtered Demand Sources') {
        clonedActiveFilters.demand = '';
      }
    }

    // update states
    unstable_batchedUpdates(() => {
      setFilters({ ...filters, ...{ [key]: allOptions }, ...updatedFilters });
      setSelectedFilters((selectedFilters) => ({
        ...selectedFilters,
        ...{ [key]: selectedOptions },
        ...updatedSelectedFilters,
      }));
      setActiveFilters(clonedActiveFilters);
      setOpenFilter('');
      setFilterModalInfo({} as Record<string, IListItem>);
    });
    overviewCfg.defaultState.activeFilters = clonedActiveFilters;

    track('Mobile Dash Live - Use Filter', { isSuperUser, filter: key }, user.user);
  };

  const handleClearAllFilters = () => {
    const selectedFilterKeys = Object.keys(selectedFilters);
    selectedFilterKeys.forEach((filterKey) => {
      const selectedItemKeys = Object.keys(selectedFilters[filterKey]);
      selectedItemKeys.forEach((key) => {
        filters[filterKey][key].selected = false;
      });
    });
    unstable_batchedUpdates(() => {
      setSelectedFilters({});
      setActiveFilters(clearedActiveFilters);
    });
    overviewCfg.defaultState.activeFilters = clearedActiveFilters;
  };

  const handleSelectDateRange = (selectedDateRange: number) => {
    setTimeRange(selectedDateRange);
    defaultState.timeRange = selectedDateRange;
    track(
      'Mobile Dash Live - Select Time Range',
      { isSuperUser, dateRange: findTimeRange(selectedDateRange).label },
      user.user,
    );
  };

  const handleOnChangeCardMetric = (
    metricKey: string,
    index: number,
    isDisplayedOnChart: boolean = false,
  ) => {
    const clonedDisplayedMetricKeys = [...displayedMetricKeys];
    clonedDisplayedMetricKeys[index] = metricKey as keyof IDashboardHomeCardMetrics;
    setDisplayedMetricKeys(clonedDisplayedMetricKeys);
    defaultState.displayedMetricKeys = clonedDisplayedMetricKeys;
    track('Mobile Dash Live - Change Card Metric', { isSuperUser, metric: metricKey }, user.user);
    if (isDisplayedOnChart) {
      handleOnClickCard(metricKey, index);
    }
  };

  // we set this as an array to prepare for multi-metric charts in v2
  // for now we only have one metric active
  const handleOnClickCard = (metricKey: string, cardIndex: number) => {
    const key = metricKey as keyof IDashboardHomeCardMetrics;
    const filteredMetrics = activeChartMetrics.filter((metric) => metric.key === key);
    if (isEmpty(filteredMetrics)) {
      const selectedMetric =
        overviewCfg.cards.metrics[metricKey as keyof IDashboardHomeCardMetrics];
      const updatedActiveMetrics = {
        key: metricKey,
        label: selectedMetric.label,
        valueType: selectedMetric.valueType,
        chartColorIndex: cardIndex,
        chartType: selectedMetric.chartType,
      };
      setActiveChartMetrics([updatedActiveMetrics]);
      defaultState.activeChartMetrics = [updatedActiveMetrics];
      track(
        'Mobile Dash Live - Display Metric On Chart',
        { isSuperUser, metric: metricKey },
        user.user,
      );
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarMessage('');
  };

  return (
    <Panels max vertical relative>
      {/* DASHBOARD SELECT AND FILTERS */}
      <Panel
        style={{
          position: 'sticky',
          top: 0,
          backgroundColor,
          zIndex: 2,
        }}
      >
        <Rows>
          <Row noPadding style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}>
            <Cols
              horizontalPadding
              smallVerticalPadding
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Col>
                <MobileNativeSelect
                  defaultValue={EDashboardTab.Live}
                  options={tabs}
                  handleChange={handleChangeTab}
                  size='large'
                  style={{ minWidth: '128px' }}
                />
              </Col>
              {/* BUTTONS */}
              <Col>
                <FilterSelector
                  activeOption={activeFilterSection}
                  options={mobileFilterSelections}
                  handleClick={setActiveFilterSection}
                />
              </Col>
            </Cols>
          </Row>
          {/* FILTERS */}
          <Row
            noPadding
            style={{
              display: activeFilterSection === EMobileFilter.Filters ? 'flex' : 'none',
              borderBottom: `1px solid ${publicaColors.lightGray}`,
            }}
          >
            <Div horizontalPadding smallVerticalPadding style={{ overflow: 'hidden' }}>
              <MobileFiltersRow
                cfg={overviewCfg.filters}
                handleOpenFilter={handleOpenFilter}
                activeFilters={activeFilters}
                handleClearAll={handleClearAllFilters}
                enableClearAll={isClearAllDisplayed}
              />
            </Div>
          </Row>
          {/* TIME RANGE */}
          <Row
            noPadding
            style={{
              display: activeFilterSection === EMobileFilter.Calendar ? 'flex' : 'none',
              borderBottom: `1px solid ${publicaColors.lightGray}`,
            }}
          >
            <Div horizontalPadding smallVerticalPadding style={{ overflow: 'hidden' }}>
              <MobileTimeRangeSelector
                selectedRange={timeRange}
                handleClick={handleSelectDateRange}
              />
            </Div>
          </Row>
        </Rows>
      </Panel>
      <Panel>
        <Rows>
          <Row smallVerticalPadding>
            <Text small bold>
              Key Metrics
            </Text>
          </Row>
          <Row noVerticalPadding>
            <MobileCards
              availableMetrics={extractViableLiveMetrics(
                overviewCfg.cards,
                liveCfg.cards.groupings[cardGroup],
              )}
              displayedMetricKeys={displayedMetricKeys}
              cardMetrics={data.CardMetrics}
              loadState={loadState}
              activeChartMetrics={activeChartMetrics}
              handleOnClickCard={handleOnClickCard}
              handleOnChangeCardMetric={handleOnChangeCardMetric}
            />
          </Row>
          <Row paddingBottom='0px'>
            <Text bold style={{ textAlign: 'center' }}>
              {activeChartMetrics[0].label}
            </Text>
          </Row>
          <Row>
            <MobileCompareChart
              loadState={loadState}
              data={formatDataForMobileCompareChart(data.LiveData)}
              metrics={activeChartMetrics}
              metricsConfig={overviewCfg.cards.metrics}
              grouping={0}
            />
          </Row>
        </Rows>
      </Panel>
      {subCharts.map((chart) => {
        return (
          <Panel>
            <MobileDashboardSubChart
              title={chart.title}
              loadState={loadState}
              data={extractDataForMultiLineChart(data.LiveData || [], Object.keys(chart.metrics))}
              metricsCfg={chart.metrics}
              orderedMetricKeys={chart.orderedMetricKeys}
              activeFilters={activeFilters}
            />
          </Panel>
        );
      })}
      <FilterModal
        open={!!openFilter}
        handleClose={handleCloseFilter}
        filterKey={openFilter}
        options={filterModalInfo}
        handleAccept={handleAcceptFilter}
        selectedLimit={(openFilter && overviewCfg.filters[openFilter].limit) || undefined}
        displayID={(openFilter && overviewCfg.filters[openFilter].displayID) || false}
        currentSelected={selectedFilters[openFilter]}
      />
      <SnackbarView
        autoHideDuration={12000}
        open={!!snackbarMessage}
        error={!!snackbarError}
        onClose={handleSnackbarClose}
        messageColor=''
        backgroundColor=''
        closeColor=''
      >
        {snackbarMessage}
      </SnackbarView>
      <PanelBreak height='40px' />
    </Panels>
  );
};
